(() => {
  const ROOT_SELECTOR = '[data-hpg-band-quality-scroll]';
  const COPY_SELECTOR = '[data-hpg-band-quality-copy]';
  const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
  const MOBILE_QUERY = '(max-width: 749px)';
  const START_GAP = 24;
  const IMAGE_START_X = 14;
  const IMAGE_END_X = -8;
  const REVEAL_THRESHOLD = 0.26;

  class HpgBandQualityReveal {
    constructor(root) {
      this.root = root;
      this.hero = root.querySelector('.hpg-band-quality__hero');
      this.stage = root.querySelector('.hpg-band-quality__stage');
      this.copyBlocks = Array.from(root.querySelectorAll(COPY_SELECTOR));
      this.startOffsets = new Map();
      this.hasRevealed = false;
      this.observer = null;
      this.reducedMotion = window.matchMedia(REDUCED_MOTION_QUERY);
      this.mobileQuery = window.matchMedia(MOBILE_QUERY);

      this.onResize = this.onResize.bind(this);
      this.onReducedMotionChange = this.onReducedMotionChange.bind(this);
      this.onMobileQueryChange = this.onMobileQueryChange.bind(this);
    }

    connect() {
      if (!this.hero || !this.stage || this.copyBlocks.length === 0) return;
      if (this.root.dataset.hpgBandQualityScrollReady === 'true') return;

      this.root.dataset.hpgBandQualityScrollReady = 'true';
      window.addEventListener('resize', this.onResize);
      this.reducedMotion.addEventListener?.('change', this.onReducedMotionChange);
      this.mobileQuery.addEventListener?.('change', this.onMobileQueryChange);

      this.measure();
      this.applyProgress(this.reducedMotion.matches ? 1 : 0);
      this.root.offsetHeight;
      this.root.classList.add('is-scroll-controlled');

      if (this.reducedMotion.matches) {
        this.root.classList.add('is-revealed');
        return;
      }

      this.observe();
    }

    disconnect() {
      window.removeEventListener('resize', this.onResize);
      this.reducedMotion.removeEventListener?.('change', this.onReducedMotionChange);
      this.mobileQuery.removeEventListener?.('change', this.onMobileQueryChange);
      this.observer?.disconnect();
      this.observer = null;
      delete this.root.dataset.hpgBandQualityScrollReady;
      this.root.classList.remove('is-scroll-controlled', 'is-revealed');
    }

    measure() {
      const stageHeight = this.stage.getBoundingClientRect().height || window.innerHeight;

      this.copyBlocks.forEach((block) => {
        const finalTop = block.offsetTop;
        this.startOffsets.set(block, Math.max(stageHeight - finalTop + START_GAP, 0));
      });
    }

    observe() {
      this.observer?.disconnect();

      if (!('IntersectionObserver' in window)) {
        this.reveal();
        return;
      }

      this.observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= REVEAL_THRESHOLD)) {
          this.reveal();
        }
      }, {
        rootMargin: '0px 0px -14% 0px',
        threshold: [REVEAL_THRESHOLD],
      });
      this.observer.observe(this.hero);
    }

    reveal() {
      if (this.hasRevealed) return;

      this.hasRevealed = true;
      this.applyProgress(1);
      this.root.classList.add('is-revealed');
      this.observer?.disconnect();
      this.observer = null;
    }

    applyProgress(progress) {
      if (this.mobileQuery.matches) {
        this.root.style.setProperty('--hpg-band-quality-image-x', '-34%');
        this.root.style.setProperty('--hpg-band-quality-image-y', '-100px');
        this.root.style.setProperty('--hpg-band-quality-image-scale', '4.5');
      } else {
        const imageX = Math.round((IMAGE_END_X + ((IMAGE_START_X - IMAGE_END_X) * (1 - progress))) * 100) / 100;
        this.root.style.setProperty('--hpg-band-quality-image-x', `${imageX}%`);
        this.root.style.setProperty('--hpg-band-quality-image-y', 'var(--hpg-band-quality-image-offset)');
        this.root.style.setProperty('--hpg-band-quality-image-scale', '1.98');
      }

      this.root.style.setProperty('--hpg-band-quality-title-opacity', progress.toFixed(3));
      this.root.style.setProperty('--hpg-band-quality-top-opacity', progress.toFixed(3));
      this.root.style.setProperty('--hpg-band-quality-body-opacity', progress.toFixed(3));

      this.copyBlocks.forEach((block) => {
        const startOffset = this.mobileQuery.matches ? 0 : this.startOffsets.get(block) || 0;
        const y = Math.round(startOffset * (1 - progress));
        block.style.setProperty('--hpg-band-quality-copy-y', `${y}px`);
        block.style.setProperty('--hpg-band-quality-copy-opacity', progress.toFixed(3));
      });
    }

    resetReveal() {
      this.observer?.disconnect();
      this.observer = null;
      this.hasRevealed = false;
      this.root.classList.remove('is-revealed');
      this.measure();
      this.applyProgress(this.reducedMotion.matches ? 1 : 0);

      if (this.reducedMotion.matches) {
        this.root.classList.add('is-revealed');
      } else {
        this.observe();
      }
    }

    onResize() {
      this.measure();
      this.applyProgress(this.hasRevealed || this.reducedMotion.matches ? 1 : 0);
    }

    onReducedMotionChange() {
      this.resetReveal();
    }

    onMobileQueryChange() {
      this.resetReveal();
    }
  }

  const instances = new WeakMap();

  const getRoots = (container) => {
    const roots = [];
    if (container.matches?.(ROOT_SELECTOR)) roots.push(container);
    container.querySelectorAll?.(ROOT_SELECTOR).forEach((root) => roots.push(root));
    return roots;
  };

  const setup = (container = document) => {
    getRoots(container).forEach((root) => {
      if (instances.has(root)) return;
      const instance = new HpgBandQualityReveal(root);
      instances.set(root, instance);
      instance.connect();
    });
  };

  const teardown = (container = document) => {
    getRoots(container).forEach((root) => {
      const instance = instances.get(root);
      if (!instance) return;
      instance.disconnect();
      instances.delete(root);
    });
  };

  document.addEventListener('DOMContentLoaded', () => setup());
  document.addEventListener('shopify:section:load', (event) => setup(event.target));
  document.addEventListener('shopify:section:unload', (event) => teardown(event.target));
})();
